import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { apiKey } from "../../config/config";
import { toast, ToastContainer } from "react-toastify";

export default function ApplyContract({ lang = {} }) {
  const [branches, setBranches] = useState([]);
  const [branchItems, setBranchItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);

  const [formData, setFormData] = useState({
    branch_id: "",
    contract_type: "installment", // "installment" or "future"
    item_id: "",
    item_name: "",
    quantity: 1,
    unit_price: "",
    total_amount: "",
    schedule_type: "Monthly",
    no_of_installments: 12,
    notes: "",
  });

  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Load branches list on mount
  useEffect(() => {
    axios
      .get(`${apiKey}/customer-panel/branches`)
      .then((res) => {
        const branchList = res.data.branches || [];
        setBranches(branchList);
        if (branchList.length > 0) {
          const firstBranchId = branchList[0].id;
          setFormData((prev) => ({ ...prev, branch_id: firstBranchId }));
          fetchBranchItems(firstBranchId);
        }
      })
      .catch((err) => console.error(err));
  }, []);

  // Fetch items for selected branch
  const fetchBranchItems = (branchId) => {
    if (!branchId) {
      setBranchItems([]);
      return;
    }
    setItemsLoading(true);
    axios
      .get(`${apiKey}/customer-panel/branch-items/${branchId}`)
      .then((res) => {
        setBranchItems(res.data.items || []);
        setItemsLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setBranchItems([]);
        setItemsLoading(false);
      });
  };

  const handleBranchChange = (e) => {
    const selectedBranchId = e.target.value;
    setFormData((prev) => ({
      ...prev,
      branch_id: selectedBranchId,
      item_id: "",
      item_name: "",
      unit_price: "",
      total_amount: "",
      quantity: 1,
    }));
    fetchBranchItems(selectedBranchId);
  };

  const handleContractTypeChange = (e) => {
    const type = e.target.value;
    setFormData((prev) => ({
      ...prev,
      contract_type: type,
      // If future contract type selected, installments must be fixed to 1
      no_of_installments: type === "future" ? 1 : prev.no_of_installments > 1 ? prev.no_of_installments : 12,
    }));
  };

  const handleItemSelect = (e) => {
    const selectedId = e.target.value;
    if (!selectedId) {
      setFormData((prev) => ({
        ...prev,
        item_id: "",
        item_name: "",
        unit_price: "",
        total_amount: "",
      }));
      return;
    }

    const selectedObj = branchItems.find((item) => String(item.id) === String(selectedId));
    if (selectedObj) {
      const price = Number(selectedObj.inventory_price || 0);
      const qty = Number(formData.quantity) || 1;
      setFormData((prev) => ({
        ...prev,
        item_id: selectedObj.id,
        item_name: selectedObj.inventory_name,
        unit_price: price,
        total_amount: (price * qty).toFixed(2),
      }));
    }
  };

  const handleQuantityChange = (e) => {
    const qty = Math.max(1, parseInt(e.target.value) || 1);
    const unitPrice = Number(formData.unit_price) || 0;
    setFormData((prev) => ({
      ...prev,
      quantity: qty,
      total_amount: (unitPrice * qty).toFixed(2),
    }));
  };

  const handleUnitPriceChange = (e) => {
    const price = e.target.value;
    const qty = Number(formData.quantity) || 1;
    setFormData((prev) => ({
      ...prev,
      unit_price: price,
      total_amount: price !== "" ? (Number(price) * qty).toFixed(2) : "",
    }));
  };

  const handleTotalAmountChange = (e) => {
    const total = e.target.value;
    setFormData((prev) => ({
      ...prev,
      total_amount: total,
    }));
  };

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.branch_id) {
      toast.error("Please select a Branch.");
      return;
    }

    if (!formData.total_amount || Number(formData.total_amount) <= 0) {
      toast.error("Please enter a valid Contract Amount.");
      return;
    }

    const session = JSON.parse(sessionStorage.getItem("aman-customer") || "{}");
    const token = session.token;

    if (!token) {
      toast.error("User session expired. Please log in again.");
      return;
    }

    setLoading(true);

    axios
      .post(`${apiKey}/customer-panel/apply-contract`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setLoading(false);
        toast.success("Contract Application Submitted Successfully!");
        setTimeout(() => {
          navigate("/customer-panel/applications");
        }, 1500);
      })
      .catch((err) => {
        setLoading(false);
        console.error(err);
        toast.error(err.response?.data?.Error || "Failed to submit application.");
      });
  };

  return (
    <div className="container-fluid px-4 py-3">
      <ToastContainer />
      <div className="pagetitle mb-4">
        <h1 className="fw-bold">{lang.Apply_For_Contract || "Apply for Contract"}</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">Home</li>
            <li className="breadcrumb-item active">Apply for Contract</li>
          </ol>
        </nav>
      </div>

      <div className="row justify-content-center">
        <div className="col-lg-10">
          <div className="card shadow-sm border-0 rounded-4">
            <div className="card-header bg-primary text-white py-3 rounded-top-4">
              <h5 className="fw-bold m-0">
                <i className="fa fa-file-contract me-2"></i> Submit Contract Application
              </h5>
            </div>
            <div className="card-body p-4">
              <form onSubmit={handleSubmit}>
                <div className="row g-3">
                  {/* Select Branch */}
                  <div className="col-md-6">
                    <label className="form-label fw-bold small text-secondary">
                      Select Target Branch <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select form-select-lg fs-6"
                      name="branch_id"
                      value={formData.branch_id}
                      onChange={handleBranchChange}
                      required
                    >
                      <option value="">-- Select Branch --</option>
                      {branches.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.branch_name} ({b.city || b.branch_location || "Branch"})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Contract Type (Only 2 types: Installment vs Future) */}
                  <div className="col-md-6">
                    <label className="form-label fw-bold small text-secondary">
                      Contract Type <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select form-select-lg fs-6"
                      name="contract_type"
                      value={formData.contract_type}
                      onChange={handleContractTypeChange}
                      required
                    >
                      <option value="installment">Installment Contract (عقد تقسيط)</option>
                      <option value="future">Future Contract (عقد آجل - دفعة واحدة)</option>
                    </select>
                  </div>

                  {/* Select Branch Item */}
                  <div className="col-md-6">
                    <label className="form-label fw-bold small text-secondary">
                      Select Item from Branch
                    </label>
                    <select
                      className="form-select"
                      value={formData.item_id}
                      onChange={handleItemSelect}
                      disabled={itemsLoading || branchItems.length === 0}
                    >
                      <option value="">
                        {itemsLoading
                          ? "Loading branch items..."
                          : branchItems.length === 0
                          ? "-- No Items Available in Branch --"
                          : "-- Select Item --"}
                      </option>
                      {branchItems.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.inventory_name} - Price: SAR {item.inventory_price} (Stock: {item.inventory_quantity})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Item Quantity */}
                  <div className="col-md-3">
                    <label className="form-label fw-bold small text-secondary">Quantity</label>
                    <input
                      type="number"
                      className="form-control"
                      min="1"
                      name="quantity"
                      value={formData.quantity}
                      onChange={handleQuantityChange}
                    />
                  </div>

                  {/* Unit Price */}
                  <div className="col-md-3">
                    <label className="form-label fw-bold small text-secondary">Unit Price (SAR)</label>
                    <input
                      type="number"
                      className="form-control"
                      placeholder="0.00"
                      name="unit_price"
                      value={formData.unit_price}
                      onChange={handleUnitPriceChange}
                    />
                  </div>

                  {/* Total Contract Amount */}
                  <div className="col-md-6">
                    <label className="form-label fw-bold small text-secondary">
                      Total Contract Value (SAR) <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text bg-light fw-bold">SAR</span>
                      <input
                        type="number"
                        className="form-control form-control-lg fs-5 fw-bold text-primary"
                        placeholder="e.g. 10000"
                        name="total_amount"
                        value={formData.total_amount}
                        onChange={handleTotalAmountChange}
                        required
                      />
                    </div>
                  </div>

                  {/* Schedule Type */}
                  <div className="col-md-3">
                    <label className="form-label fw-bold small text-secondary">Schedule Type</label>
                    <select
                      className="form-select"
                      name="schedule_type"
                      value={formData.schedule_type}
                      onChange={handleChange}
                    >
                      <option value="Monthly">Monthly (شهري)</option>
                      <option value="Weekly">Weekly (أسبوعي)</option>
                      <option value="Half-Yearly">Half-Yearly (نصف سنوي)</option>
                      <option value="Yearly">Yearly (سنوي)</option>
                    </select>
                  </div>

                  {/* Number of Installments */}
                  <div className="col-md-3">
                    <label className="form-label fw-bold small text-secondary">
                      Number of Installments
                    </label>
                    {formData.contract_type === "future" ? (
                      <input
                        type="number"
                        className="form-control bg-light"
                        value={1}
                        readOnly
                      />
                    ) : (
                      <select
                        className="form-select"
                        name="no_of_installments"
                        value={formData.no_of_installments}
                        onChange={handleChange}
                      >
                        <option value="2">2 Installments</option>
                        <option value="3">3 Installments</option>
                        <option value="6">6 Installments</option>
                        <option value="12">12 Installments (1 Year)</option>
                        <option value="18">18 Installments</option>
                        <option value="24">24 Installments (2 Years)</option>
                        <option value="36">36 Installments (3 Years)</option>
                      </select>
                    )}
                    {formData.contract_type === "future" && (
                      <div className="form-text small text-muted">
                        Future contracts require a single full payment (1 installment).
                      </div>
                    )}
                  </div>

                  {/* Additional Notes / Application Details */}
                  <div className="col-12 mt-3">
                    <label className="form-label fw-bold small text-secondary">
                      Application Notes & Remarks
                    </label>
                    <textarea
                      className="form-control"
                      rows="3"
                      placeholder="Enter any notes or remarks for the branch manager..."
                      name="notes"
                      value={formData.notes}
                      onChange={handleChange}
                    ></textarea>
                  </div>

                  <div className="col-12 text-end mt-4">
                    <button
                      type="submit"
                      className="btn btn-primary btn-lg px-5 fw-bold shadow-sm"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          Submitting...
                        </>
                      ) : (
                        "Submit Application"
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
